/**
 * GlassBoxTrace — canonical explainability primitive (issue #17).
 *
 * Accepts a `trace: GlassBoxTrace` prop and renders a collapsed-by-default
 * disclosure footer showing:
 *   - Confidence rationale
 *   - Cited evidence with typed provenance chips (drillable via href)
 *   - Alternatives considered (option + why not chosen)
 *   - Counterfactual anchors (condition + would-change-to)
 *
 * This component is the canonical primitive. `AgentGlassBoxDetail` derives a
 * `GlassBoxTrace` from an `AgentTask` and delegates rendering here.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Scale, Compass, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
    GlassBoxTrace as GlassBoxTraceType,
    TypedCitation,
    Alternative,
    Counterfactual,
} from '@/data/types';

// ─── Citation chip colours by kind ────────────────────────────────────────────

const KIND_CHIP: Record<string, { label: string; cls: string }> = {
    firm_policy: {
        label: 'firm policy',
        cls: 'bg-[rgb(var(--color-accent)/0.12)] text-[rgb(var(--color-accent))]',
    },
    market_data: {
        label: 'market data',
        cls: 'bg-[rgb(var(--color-rag-green)/0.12)] text-[rgb(var(--color-rag-green))]',
    },
    kb_note: {
        label: 'KB note',
        cls: 'bg-[rgb(var(--color-rag-amber)/0.12)] text-[rgb(var(--color-rag-amber))]',
    },
    fca_handbook: {
        label: 'FCA handbook',
        cls: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    },
    transcript: { label: 'transcript', cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400' },
    audit_log: {
        label: 'audit log',
        cls: 'bg-slate-500/10 text-[rgb(var(--color-text-secondary))]',
    },
    fund_policy: {
        label: 'fund policy',
        cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    },
};

function CitationKindChip({ kind }: { kind: TypedCitation['kind'] }) {
    const chip = KIND_CHIP[kind] ?? {
        label: kind.replace(/_/g, ' '),
        cls: 'bg-slate-500/10 text-[rgb(var(--color-text-secondary))]',
    };
    return (
        <span
            className={cn(
                'inline-block text-overline uppercase px-1.5 py-0.5 rounded-sm font-medium leading-none',
                chip.cls,
            )}
        >
            {chip.label}
        </span>
    );
}

// ─── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, label }: { icon: typeof BookOpen; label: string }) {
    return (
        <div className="flex items-center gap-1.5 text-card-title text-[rgb(var(--color-text-primary))]">
            <Icon className="w-3 h-3" strokeWidth={2} />
            {label}
        </div>
    );
}

// ─── Confidence rationale ──────────────────────────────────────────────────────

function ConfidenceSection({ rationale }: { rationale: string }) {
    return (
        <div className="flex flex-col gap-1">
            <SectionHeader icon={Sparkles} label="Confidence rationale" />
            <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug">
                {rationale}
            </p>
        </div>
    );
}

// ─── Cited evidence ────────────────────────────────────────────────────────────

function CitedEvidenceSection({ citations }: { citations: TypedCitation[] }) {
    return (
        <div className="flex flex-col gap-1.5">
            <SectionHeader icon={BookOpen} label={`Cited evidence (${citations.length})`} />
            <ul className="flex flex-col gap-1">
                {citations.map((c, i) => (
                    <li key={`${c.kind}-${c.ref}-${i}`} className="flex flex-col gap-0.5">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-annotation font-medium text-[rgb(var(--color-text-primary))]">
                                {c.label}
                            </span>
                            <CitationKindChip kind={c.kind} />
                            {c.href && (
                                <a
                                    href={c.href}
                                    className="text-annotation text-[rgb(var(--color-accent))] hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    open ↗
                                </a>
                            )}
                        </div>
                        {c.ref && c.ref !== c.label && (
                            <span className="text-overline font-mono text-[rgb(var(--color-text-tertiary))]">
                                ref: {c.ref}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── Alternatives considered ───────────────────────────────────────────────────

function AlternativesSection({ alternatives }: { alternatives: Alternative[] }) {
    return (
        <div className="flex flex-col gap-1.5">
            <SectionHeader icon={Scale} label="Alternatives considered" />
            <ul className="flex flex-col gap-1">
                {alternatives.map((a, i) => (
                    <li key={i} className="flex flex-col gap-0.5">
                        <div className="text-annotation text-[rgb(var(--color-text-primary))]">
                            <span className="text-[rgb(var(--color-text-tertiary))]">Alt:</span>{' '}
                            {a.option}
                        </div>
                        <div className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug">
                            <span className="text-[rgb(var(--color-text-tertiary))]">
                                Not chosen because:
                            </span>{' '}
                            {a.whyNotChosen}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── Counterfactual anchors ────────────────────────────────────────────────────

function CounterfactualsSection({ counterfactuals }: { counterfactuals: Counterfactual[] }) {
    return (
        <div className="flex flex-col gap-1.5">
            <SectionHeader icon={Compass} label="Counterfactual anchors" />
            <ul className="flex flex-col gap-1">
                {counterfactuals.map((cf, i) => (
                    <li key={i} className="flex flex-col gap-0.5">
                        <div className="text-annotation text-[rgb(var(--color-text-primary))]">
                            <span className="text-[rgb(var(--color-text-tertiary))]">If:</span>{' '}
                            {cf.condition}
                        </div>
                        <div className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug">
                            <span className="text-[rgb(var(--color-text-tertiary))]">Then:</span>{' '}
                            {cf.wouldChangeTo}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface GlassBoxTraceProps {
    trace: GlassBoxTraceType;
    defaultOpen?: boolean;
}

export default function GlassBoxTrace({ trace, defaultOpen = false }: GlassBoxTraceProps) {
    const [open, setOpen] = useState(defaultOpen);

    const hasContent =
        trace.confidenceRationale ||
        trace.citedEvidence.length > 0 ||
        trace.alternatives.length > 0 ||
        trace.counterfactuals.length > 0;

    if (!hasContent) return null;

    const confidencePct =
        trace.confidenceScore !== undefined ? Math.round(trace.confidenceScore * 100) : null;

    return (
        <section
            className={cn(
                'rounded-md border border-[rgb(var(--color-divider))]',
                'bg-[rgb(var(--color-surface-secondary)/0.6)]',
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left',
                    'text-card-title text-[rgb(var(--color-text-secondary))]',
                    'hover:text-[rgb(var(--color-text-primary))] transition-colors',
                )}
                aria-expanded={open}
            >
                {open ? (
                    <ChevronDown className="w-3 h-3" strokeWidth={2} />
                ) : (
                    <ChevronRight className="w-3 h-3" strokeWidth={2} />
                )}
                <span className="flex-1">Glass-box trace</span>
                {confidencePct !== null && (
                    <span
                        className={cn(
                            'text-annotation font-mono tabular-nums',
                            confidencePct >= 80
                                ? 'text-[rgb(var(--color-rag-green))]'
                                : confidencePct >= 60
                                  ? 'text-[rgb(var(--color-rag-amber))]'
                                  : 'text-[rgb(var(--color-rag-red))]',
                        )}
                    >
                        confidence {confidencePct}%
                    </span>
                )}
            </button>

            {open && (
                <div className="flex flex-col gap-3 px-3 pb-3 border-t border-[rgb(var(--color-divider)/0.6)] pt-3">
                    {trace.confidenceRationale && (
                        <ConfidenceSection rationale={trace.confidenceRationale} />
                    )}
                    {trace.citedEvidence.length > 0 && (
                        <CitedEvidenceSection citations={trace.citedEvidence} />
                    )}
                    {trace.alternatives.length > 0 && (
                        <AlternativesSection alternatives={trace.alternatives} />
                    )}
                    {trace.counterfactuals.length > 0 && (
                        <CounterfactualsSection counterfactuals={trace.counterfactuals} />
                    )}
                </div>
            )}
        </section>
    );
}
